// phase-compiler-tab-pins — the composed Lifecycle tab panel.
//
// A small wrapper that puts an HONEST quick overview FIRST — the document's
// current lifecycle operation (via the canonical DocumentBadge, which renders an
// honest "Unknown" rather than a fabricated "NEW") and its recorded
// previous-operation history (the existing LifecycleTab, with its own
// loading / error / empty / single-entry semantics untouched). Both are ALWAYS
// visible; neither is inferred.
//
// The operation editor (which itself contains the related-document matching,
// evidence and justification review) starts COLLAPSED under a native <details>
// disclosure. Once opened it is RETAINED (the `hasOpened` latch keeps it mounted
// even after the disclosure is closed) so collapsing never discards an in-flight
// draft. The parent keys this wrapper by the document id, so the disclosure
// resets to collapsed on a genuine document change — but an ordinary tab switch
// keeps the whole panel mounted (CompilerWorkspace never unmounts a visited
// panel), so the draft survives that too.
//
// This composes existing pieces and introduces NO lifecycle business rules of
// its own — the operation set, badge mapping and history all stay in their
// canonical homes.

import { useState, type CSSProperties, type ReactElement } from 'react';
import type { Document } from '@/api/v2Client';
import { DocumentBadge } from './DocumentBadge';
import { LifecycleOperationEditor } from './LifecycleOperationEditor';
import { LifecycleTab } from './inspector/LifecycleTab';
import type { LifecycleOperation } from './lifecycle';

export interface LifecycleWorkspacePanelProps {
  readonly submissionId: string;
  /** The selected document — the parent renders its own empty state when none. */
  readonly document: Document;
  readonly canEdit: boolean;
  readonly onSuccess: (newOperation: LifecycleOperation) => void;
  readonly onError: (message: string) => void;
}

const overviewStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const currentRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
};

const currentLabelStyle: CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-muted)',
  fontWeight: 700,
};

const detailsStyle: CSSProperties = {
  marginTop: 12,
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--surface2)',
};

const summaryStyle: CSSProperties = {
  cursor: 'pointer',
  padding: '8px 10px',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
  userSelect: 'none',
};

const detailsBodyStyle: CSSProperties = {
  padding: '0 10px 10px 10px',
};

export function LifecycleWorkspacePanel({
  submissionId,
  document,
  canEdit,
  onSuccess,
  onError,
}: LifecycleWorkspacePanelProps): ReactElement {
  const [open, setOpen] = useState(false);
  // Latch: keep the editor mounted after the FIRST open so that collapsing the
  // disclosure never discards a staged operation / reason / prior-doc selection.
  const [hasOpened, setHasOpened] = useState(false);
  // Bumped on a successful save so the history re-fetches in place and shows the
  // newly-recorded operation instead of a stale timeline (Kimi F3).
  const [historyNonce, setHistoryNonce] = useState(0);

  const handleSaved = (newOperation: LifecycleOperation): void => {
    setHistoryNonce((n) => n + 1);
    onSuccess(newOperation);
  };

  return (
    <div data-testid="workspace-lifecycle">
      <div data-testid="lifecycle-overview" style={overviewStyle}>
        <div style={currentRowStyle}>
          <span style={currentLabelStyle}>Current operation</span>
          <DocumentBadge operation={document.LifecycleOperation as string | null | undefined} size="md" />
        </div>
        <LifecycleTab submissionId={submissionId} documentId={document.Id} refreshNonce={historyNonce} />
      </div>

      <details
        data-testid="lifecycle-edit-details"
        open={open}
        onToggle={(event) => {
          const next = event.currentTarget.open;
          setOpen(next);
          if (next) setHasOpened(true);
        }}
        style={detailsStyle}
      >
        <summary data-testid="lifecycle-edit-summary" style={summaryStyle}>
          Edit operation &amp; related documents
        </summary>
        <div style={detailsBodyStyle}>
          {(open || hasOpened) && (
            <LifecycleOperationEditor
              documentId={document.Id}
              sourceDocument={document}
              submissionId={submissionId}
              sectionCode={document.CtdSectionCode ?? null}
              currentOperation={document.LifecycleOperation as string | null | undefined}
              rowVersion={document.RowVersion}
              canEdit={canEdit}
              onSuccess={handleSaved}
              onError={onError}
            />
          )}
        </div>
      </details>
    </div>
  );
}
